import React, { useEffect, useState, useCallback } from "react";
import {
  getUncuratedSwapsPaginated,
  markSwapAsCurated,
  updateSwapCacheEntry,
  deleteSwapCacheEntry
} from "../firebase";
import { toast } from "react-toastify";

function AdminPanel({ user }) {
  const [swaps, setSwaps] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSwaps = useCallback(
    async (append = false) => {
      setLoading(true);
      try {
        const { swaps: newSwaps, lastDoc: newLastDoc } =
          await getUncuratedSwapsPaginated(lastDoc);

        // prevent duplicates (e.g., same doc already exists)
        const newIds = new Set(newSwaps.map((s) => s.id));
        const filteredPrev = swaps.filter((s) => !newIds.has(s.id));

        setSwaps(append ? [...filteredPrev, ...newSwaps] : newSwaps);
        setLastDoc(newLastDoc);
        setHasMore(newSwaps.length === 5);
      } catch (err) {
        toast.error("Failed to load swaps.");
        console.error(err);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastDoc]);

  useEffect(() => {
    loadSwaps(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleMarkCurated = async (docId) => {
    await markSwapAsCurated(docId, user?.email || "unknown");
    setSwaps((prev) => prev.filter((s) => s.id !== docId));
    toast.success("Marked as curated ✅");
  };

  const handleDeleteEntry = async (docId) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      await deleteSwapCacheEntry(docId);
      setSwaps((prev) => prev.filter((s) => s.id !== docId));
      toast.success("Entry deleted 🗑️");
    }
  };

  const handleSaveEntry = async (docId, updatedSwapOptions, updatedComment) => {
    try {
      await updateSwapCacheEntry(docId, {
        swapOptions: updatedSwapOptions,
        swapComment: updatedComment || ""
      });
      toast.success("Saved ✅");
    } catch (err) {
      toast.error("Failed to save entry.");
    }
  };

  const toggleExpand = (docId) => {
    setExpandedId((prevId) => {
      const newId = prevId === docId ? null : docId;
      setTimeout(() => {
        const el = document.getElementById(`panel-${docId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return newId;
    });
  };


  const updateSwapField = (docId, index, field, value) => {
    setSwaps((prev) =>
      prev.map((swap) =>
        swap.id === docId
          ? {
            ...swap,
            swapOptions: swap.swapOptions.map((opt, i) =>
              i === index ? { ...opt, [field]: value } : opt
            )
          }
          : swap
      )
    );
  };

  const removeSwapOption = (docId, index) => {
    setSwaps((prev) =>
      prev.map((swap) =>
        swap.id === docId
          ? {
            ...swap,
            swapOptions: swap.swapOptions.filter((_, i) => i !== index)
          }
          : swap
      )
    );
  };

  const addSwapOption = (docId) => {
    setSwaps((prev) =>
      prev.map((swap) =>
        swap.id === docId
          ? {
            ...swap,
            swapOptions: [
              ...swap.swapOptions,
              { swap: "", explanation: "" }
            ]
          }
          : swap
      )
    );
  };

  return (
    <div style={{ marginTop: "2rem", padding: "1rem", border: "1px solid #aaa", borderRadius: "8px" }}>
      <h3>🛠️ Admin Panel – Curate Swap Cache</h3>

      {swaps.map((swap) => (
        <div
          id={`panel-${swap.id}`}
          key={swap.id}
          style={{
            marginBottom: "1rem",
            padding: "0.75rem",
            borderBottom: "1px solid #ddd",
            background: "#f9f9f9",
            borderRadius: "6px"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>{swap.food} ({swap.condition})</strong>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={() => toggleExpand(swap.id)}>
                {expandedId === swap.id ? "▲" : "▼"}
              </button>
              <button onClick={() => handleDeleteEntry(swap.id)} title="Delete Entry">🗑️</button>
            </div>
          </div>

          {expandedId === swap.id && (
            <>
              <ul style={{ marginTop: "0.5rem", listStyle: "none", padding: 0 }}>
                {swap.swapOptions.map((opt, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom: "1.5rem",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      padding: "1rem",
                      background: "#fff"
                    }}
                  >
                    <label>Swap #{i + 1}:</label><br />
                    <input
                      type="text"
                      value={opt.swap}
                      onChange={(e) =>
                        updateSwapField(swap.id, i, "swap", e.target.value)
                      }
                      style={{ width: "100%", marginBottom: "0.5rem" }}
                    />
                    <div
                      style={{
                        backgroundColor: "#f5faff",
                        border: "1px solid #cce4f6",
                        padding: "0.75rem",
                        borderRadius: "6px"
                      }}
                    >
                      <label style={{ fontWeight: "bold", color: "#036" }}>💡 Why:</label>
                      <textarea
                        value={opt.explanation}
                        rows={5}
                        style={{
                          width: "100%",
                          resize: "vertical",
                          overflow: "auto",
                          border: "none",
                          backgroundColor: "transparent",
                          fontFamily: "inherit",
                          fontSize: "0.95rem",
                          marginTop: "0.5rem"
                        }}
                        onChange={(e) =>
                          updateSwapField(swap.id, i, "explanation", e.target.value)
                        }
                      />
                    </div>
                    <button
                      onClick={() => removeSwapOption(swap.id, i)}
                      style={{ marginTop: "0.5rem" }}
                    >
                      🗑️ Remove Above Option
                    </button>
                  </li>
                ))}
              </ul>
              {swap.curatedBy && (
                <p style={{ fontSize: "0.85rem", color: "#444", marginTop: "0.25rem" }}>
                  <strong>Curated by:</strong> {swap.curatedBy}
                </p>
              )}

              <button onClick={() => addSwapOption(swap.id)}>➕ Add New Option</button>
              <div style={{ marginTop: "1.5rem" }}>
                <label style={{ fontWeight: "bold" }}>📝 Comment:</label>
                <textarea
                  rows={4}
                  value={swap.swapComment || ""}
                  onChange={(e) =>
                    setSwaps(prev =>
                      prev.map(s =>
                        s.id === swap.id
                          ? { ...s, swapComment: e.target.value }
                          : s
                      )
                    )
                  }
                  style={{
                    width: "100%",
                    fontSize: "0.85rem",
                    padding: "0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    marginTop: "0.5rem"
                  }}
                />
              </div>

              <br /><br />
              <button onClick={() => handleSaveEntry(swap.id, swap.swapOptions, swap.swapComment)}>💾 Save Changes</button>
              <button onClick={() => handleMarkCurated(swap.id)} style={{ marginLeft: "1rem" }}>✅ Mark as Curated</button>
            </>
          )}
        </div>
      ))}

      {hasMore && (
        <button onClick={() => loadSwaps(true)} disabled={loading}>
          {loading ? "Loading..." : "Load More"}
        </button>
      )}
    </div>
  );
}

export default AdminPanel;
